// Beállítások → Jelentések (2.7b): who gets which morning letter, how often,
// and with which sections. One row per address and letter; every change is
// one line of the list's log, with the admin who made it. While the list is
// empty the owner's letter goes where the alert mail goes (the line under the
// title says to whom), so nothing changed on the day this page arrived.

import { useEffect, useId, useState } from "react";
import type { FormEvent } from "react";
import { toast } from "sonner";
import { EyeIcon, MailPlusIcon } from "lucide-react";
import type { ColumnDef } from "@tanstack/react-table";
import {
  addDigestRecipient,
  changeDigestRecipient,
  fetchDigestHtml,
  fetchDigestRecipients,
  removeDigestRecipient,
} from "@/lib/api";
import type { DigestLetter, DigestLetterSpec, DigestRecipient } from "@/lib/api";
import { useLoad } from "@/lib/useLoad";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { NativeSelect } from "@/components/ui/native-select";
import { ActionDialog } from "@/components/emc/ActionDialog";
import { DataTable } from "@/components/emc/DataTable";
import { Field } from "@/components/emc/Field";
import { RowActions } from "@/components/emc/RowActions";
import { Callout, ErrorState } from "@/components/emc/states";
import { minute } from "@/components/emc/format";
import { messageOf } from "@/pages/campaigns/model";
import {
  FREQUENCY_WORD,
  LETTER_WORD,
  WEEKDAYS,
  allOn,
  blankForm,
  checkForm,
  eventLine,
  fallbackLine,
  formOf,
  frequencyLine,
  sectionsLine,
  valuesOf,
} from "@/pages/settings/digest";
import type { RecipientForm } from "@/pages/settings/digest";

/** Yesterday, as the API's `day` takes it. */
function yesterday(): string {
  const day = new Date();
  day.setDate(day.getDate() - 1);
  return `${day.getFullYear()}-${String(day.getMonth() + 1).padStart(2, "0")}-${String(day.getDate()).padStart(2, "0")}`;
}

async function openPreview(letter: DigestLetter): Promise<void> {
  const html = await fetchDigestHtml(yesterday(), letter, 7);
  const url = URL.createObjectURL(new Blob([html], { type: "text/html" }));
  window.open(url, "_blank", "noopener");
  // The tab has the blob by now; revoking later frees it without racing the open.
  window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

function RecipientDialog({
  open,
  onOpenChange,
  editing,
  rows,
  letters,
  onSaved,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  editing: DigestRecipient | null;
  rows: DigestRecipient[];
  letters: DigestLetterSpec[];
  onSaved: () => void;
}) {
  const [form, setForm] = useState<RecipientForm>(() => blankForm(letters));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const emailId = useId();
  const letterId = useId();
  const frequencyId = useId();
  const weekdayId = useId();

  useEffect(() => {
    if (open) {
      setForm(editing === null ? blankForm(letters) : formOf(editing));
      setError(null);
      setBusy(false);
    }
  }, [open, editing, letters]);

  const errors = checkForm(form, rows, editing?.id ?? null);
  const spec = letters.find((one) => one.letter === form.letter);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (busy || Object.keys(errors).length > 0) return;
    setBusy(true);
    setError(null);
    try {
      if (editing === null) await addDigestRecipient(valuesOf(form));
      else await changeDigestRecipient(editing.id, valuesOf(form));
      toast.success(editing === null ? "Címzett hozzáadva" : "Mentve", { description: form.email.trim() });
      onSaved();
      onOpenChange(false);
    } catch (raised: unknown) {
      setError(messageOf(raised));
    } finally {
      setBusy(false);
    }
  }

  const touched = form.email.trim() !== "";
  return (
    <Dialog open={open} onOpenChange={(next) => !busy && onOpenChange(next)}>
      <DialogContent>
        <form className="flex flex-col gap-4" onSubmit={(event) => void submit(event)}>
          <DialogHeader>
            <DialogTitle>{editing === null ? "Címzett hozzáadása" : "Címzett szerkesztése"}</DialogTitle>
            <DialogDescription>
              A levél reggel megy, az admin időzónája szerint. A vezetőségi levél számai heti küldésnél az
              elmúlt hét napot adják össze.
            </DialogDescription>
          </DialogHeader>
          <Field label="Email-cím" htmlFor={emailId} hint={touched ? errors.email : undefined}>
            <Input
              id={emailId}
              type="email"
              autoComplete="off"
              required
              maxLength={320}
              value={form.email}
              onChange={(event) => setForm({ ...form, email: event.target.value })}
              autoFocus
            />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Levél" htmlFor={letterId}>
              <NativeSelect
                id={letterId}
                className="w-full"
                value={form.letter}
                onChange={(event) => {
                  const letter = event.target.value as DigestLetter;
                  setForm({ ...form, letter, sections: allOn(letters, letter) });
                }}
              >
                {letters.map((one) => (
                  <option key={one.letter} value={one.letter}>
                    {LETTER_WORD[one.letter]}
                  </option>
                ))}
              </NativeSelect>
            </Field>
            <Field label="Gyakoriság" htmlFor={frequencyId}>
              <NativeSelect
                id={frequencyId}
                className="w-full"
                value={form.frequency}
                onChange={(event) => setForm({ ...form, frequency: event.target.value as RecipientForm["frequency"] })}
              >
                <option value="daily">{FREQUENCY_WORD.daily}</option>
                <option value="weekly">{FREQUENCY_WORD.weekly}</option>
              </NativeSelect>
            </Field>
          </div>
          {form.frequency === "weekly" && (
            <Field label="A hét napja" htmlFor={weekdayId} hint={errors.weekday}>
              <NativeSelect
                id={weekdayId}
                className="w-full"
                value={String(form.weekday)}
                onChange={(event) => setForm({ ...form, weekday: Number(event.target.value) })}
              >
                {WEEKDAYS.map((day, index) => (
                  <option key={day} value={index}>
                    {day}
                  </option>
                ))}
              </NativeSelect>
            </Field>
          )}
          <fieldset className="m-0 flex flex-col gap-2 border-0 p-0">
            <legend className="mb-1 text-label font-semibold">Szakaszok</legend>
            {(spec?.sections ?? []).map((section) => (
              <label key={section.key} className="flex items-center gap-2">
                <Checkbox
                  checked={form.sections[section.key] !== false}
                  onChange={(event) =>
                    setForm({ ...form, sections: { ...form.sections, [section.key]: event.target.checked } })
                  }
                />
                {section.label}
              </label>
            ))}
          </fieldset>
          <label className="flex items-center gap-2">
            <Checkbox
              checked={form.is_active}
              onChange={(event) => setForm({ ...form, is_active: event.target.checked })}
            />
            Kapja a levelet (kikapcsolva a sor megmarad, de nem megy neki semmi)
          </label>
          {error !== null && <ErrorState message={error} />}
          <DialogFooter>
            <Button variant="outline" type="button" disabled={busy} onClick={() => onOpenChange(false)}>
              Mégse
            </Button>
            <Button type="submit" disabled={busy || Object.keys(errors).length > 0}>
              {busy ? "Folyamatban…" : editing === null ? "Hozzáadás" : "Mentés"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function DigestRecipients() {
  const [{ data, error }, reload] = useLoad(fetchDigestRecipients, []);
  const [dialog, setDialog] = useState<{ editing: DigestRecipient | null } | null>(null);
  const [removing, setRemoving] = useState<DigestRecipient | null>(null);
  const [previewing, setPreviewing] = useState<DigestLetter | null>(null);
  const rows = data?.recipients ?? [];
  const letters = data?.letters ?? [];

  async function preview(letter: DigestLetter) {
    setPreviewing(letter);
    try {
      await openPreview(letter);
    } catch (cause: unknown) {
      toast.error("Az előnézet nem nyílt meg", { description: messageOf(cause) });
    } finally {
      setPreviewing(null);
    }
  }

  async function toggle(row: DigestRecipient) {
    try {
      await changeDigestRecipient(row.id, { is_active: !row.is_active });
      toast.success(row.is_active ? "Kikapcsolva" : "Bekapcsolva", { description: row.email });
      reload();
    } catch (cause: unknown) {
      toast.error("Nem sikerült", { description: messageOf(cause) });
    }
  }

  const columns: ColumnDef<DigestRecipient>[] = [
    {
      id: "email",
      header: "Cím",
      meta: { wrap: true },
      cell: ({ row }) => <span className="font-medium">{row.original.email}</span>,
    },
    { id: "letter", header: "Levél", cell: ({ row }) => LETTER_WORD[row.original.letter] },
    { id: "frequency", header: "Gyakoriság", cell: ({ row }) => frequencyLine(row.original) },
    {
      id: "sections",
      header: "Szakaszok",
      meta: { wrap: true, muted: true },
      cell: ({ row }) => sectionsLine(row.original, letters),
    },
    {
      id: "state",
      header: "Állapot",
      cell: ({ row }) =>
        row.original.is_active ? "kapja" : <span className="text-muted-foreground">kikapcsolva</span>,
    },
    {
      id: "actions",
      header: () => <span className="sr-only">Műveletek</span>,
      meta: { align: "right" },
      cell: ({ row }) => (
        <RowActions
          label={`Műveletek: ${row.original.email}`}
          actions={[
            { label: "Szerkesztés", onSelect: () => setDialog({ editing: row.original }) },
            {
              label: row.original.is_active ? "Kikapcsolás" : "Bekapcsolás",
              onSelect: () => void toggle(row.original),
            },
            { label: "Törlés", danger: true, onSelect: () => setRemoving(row.original) },
          ]}
        />
      ),
    },
  ];

  const fallback = data === null ? null : fallbackLine(data.fallback);
  return (
    <Card>
      <CardHeader>
        <CardTitle>Jelentések</CardTitle>
        <CardDescription>
          Ki kapja a reggeli levelet, melyiket és milyen gyakran. A tulajdonosi levél a küldés állapota, a
          jelzések, a teendők és a hét; a vezetőségi a hét három száma és a Klaviyo kiváltása.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        <div className="flex flex-wrap gap-2">
          <Button type="button" onClick={() => setDialog({ editing: null })} disabled={data === null}>
            <MailPlusIcon />
            Címzett hozzáadása
          </Button>
          <Button type="button" variant="outline" disabled={previewing !== null} onClick={() => void preview("owner")}>
            <EyeIcon />
            Tulajdonosi levél előnézete
          </Button>
          <Button
            type="button"
            variant="outline"
            disabled={previewing !== null}
            onClick={() => void preview("management")}
          >
            <EyeIcon />
            Vezetőségi levél előnézete
          </Button>
        </div>
        {error !== null && <ErrorState message={error} onRetry={reload} />}
        {fallback !== null && <Callout tone="info">{fallback}</Callout>}
        <DataTable
          label="Jelentések címzettjei"
          columns={columns}
          data={rows}
          loading={data === null && error === null}
          getRowId={(row) => String(row.id)}
          variant="plain"
          empty={<p className="px-4 py-3 text-muted-foreground">Még nincs címzett.</p>}
        />
        {data !== null && data.events.length > 0 && (
          <details>
            <summary className="cursor-pointer text-label text-muted-foreground">Változások</summary>
            <ul className="m-0 mt-2 flex list-none flex-col gap-1 p-0 text-label">
              {data.events.map((event) => (
                <li key={event.id}>
                  <span className="data-mono text-muted-foreground">{minute(event.occurred_at)}</span>{" "}
                  {eventLine(event)}
                </li>
              ))}
            </ul>
          </details>
        )}
      </CardContent>
      <RecipientDialog
        open={dialog !== null}
        onOpenChange={(open) => !open && setDialog(null)}
        editing={dialog?.editing ?? null}
        rows={rows}
        letters={letters}
        onSaved={reload}
      />
      <ActionDialog
        open={removing !== null}
        onOpenChange={(open) => !open && setRemoving(null)}
        title="Címzett törlése"
        description={
          removing === null
            ? undefined
            : `${removing.email} nem kapja tovább a(z) ${LETTER_WORD[removing.letter].toLowerCase()} levelet. A változás a naplóban marad.`
        }
        confirmLabel="Törlés"
        tone="danger"
        onConfirm={async () => {
          if (removing === null) return;
          await removeDigestRecipient(removing.id);
          toast.success("Törölve", { description: removing.email });
          setRemoving(null);
          reload();
        }}
      />
    </Card>
  );
}
